import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import { useGenerateCardPool } from '../../hooks';
import { formatPoints } from '../../lib/format';
import { GENERATE_MODE_LABELS, GENERATE_MODE_OPTIONS } from '../../model/labels';
import type { GenerateCardPoolRequest, GenerateMode } from '../../model/types';

interface GeneratePoolDialogProps {
  open: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
}

const toNum = (value: string): number | undefined => (value.trim() ? Number(value) : undefined);

/** Generate a free-card pool by range or mask (gated by `loyalty.manage`). */
export function GeneratePoolDialog({ open, onClose, onDone }: GeneratePoolDialogProps) {
  const { t } = useTranslation('loyalty');
  const [mode, setMode] = useState<GenerateMode>('RANGE');
  const [prefix, setPrefix] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [padLength, setPadLength] = useState('');
  const [mask, setMask] = useState('');
  const [count, setCount] = useState('');
  const [startAt, setStartAt] = useState('0');
  const [error, setError] = useState<string | null>(null);
  const generate = useGenerateCardPool();

  useEffect(() => {
    if (open) {
      setMode('RANGE');
      setPrefix('');
      setStart('');
      setEnd('');
      setPadLength('');
      setMask('');
      setCount('');
      setStartAt('0');
      setError(null);
    }
  }, [open]);

  const startNum = toNum(start);
  const endNum = toNum(end);
  const countNum = toNum(count);
  const rangeValid =
    Number.isInteger(startNum) && Number.isInteger(endNum) && (startNum as number) <= (endNum as number);
  const maskValid = mask.includes('#') && Number.isInteger(countNum) && (countNum as number) >= 1;
  const valid = mode === 'RANGE' ? rangeValid : maskValid;

  const submit = () => {
    setError(null);
    const body: GenerateCardPoolRequest =
      mode === 'RANGE'
        ? {
            mode,
            prefix: prefix.trim() || undefined,
            start: startNum,
            end: endNum,
            padLength: toNum(padLength),
          }
        : { mode, mask: mask.trim(), count: countNum, startAt: toNum(startAt) ?? 0 };
    generate.mutate(body, {
      onSuccess: (res) => {
        onDone(t('generatePoolDialog.result', { added: formatPoints(res.added), received: formatPoints(res.received) }));
        onClose();
      },
      onError: (err) => setError(err.message),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{t('generatePoolDialog.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            select
            label={t('generatePoolDialog.modeLabel')}
            value={mode}
            onChange={(e) => setMode(e.target.value as GenerateMode)}
            fullWidth
          >
            {GENERATE_MODE_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {t(GENERATE_MODE_LABELS[option])}
              </MenuItem>
            ))}
          </TextField>
          {mode === 'RANGE' ? (
            <>
              <TextField
                label={t('generatePoolDialog.prefixLabel')}
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                fullWidth
                helperText={t('generatePoolDialog.prefixHelper')}
              />
              <TextField
                label={t('generatePoolDialog.startLabel')}
                type="number"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                fullWidth
              />
              <TextField
                label={t('generatePoolDialog.endLabel')}
                type="number"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                fullWidth
              />
              <TextField
                label={t('generatePoolDialog.padLengthLabel')}
                type="number"
                value={padLength}
                onChange={(e) => setPadLength(e.target.value)}
                fullWidth
                helperText={t('generatePoolDialog.padLengthHelper')}
              />
            </>
          ) : (
            <>
              <TextField
                label={t('generatePoolDialog.maskLabel')}
                value={mask}
                onChange={(e) => setMask(e.target.value)}
                error={mask.trim().length > 0 && !mask.includes('#')}
                fullWidth
                helperText={t('generatePoolDialog.maskHelper')}
                slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
              />
              <TextField
                label={t('generatePoolDialog.countLabel')}
                type="number"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                fullWidth
              />
              <TextField
                label={t('generatePoolDialog.startAtLabel')}
                type="number"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                fullWidth
              />
            </>
          )}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={generate.isPending}>
          {t('common:action.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={!valid || generate.isPending}
          startIcon={generate.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {t('generatePoolDialog.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
